import { FileManagerState } from './useFileManager'

type FileOptions = {
  fileSize: number
  blobId: string
  metadata?: Record<string, any>
}

type LinkOptions = {
  url: string
}

type DirectoryOptions<T extends DirectoryItemType = DirectoryItemType, E = {}> = {
  children: DirectoryItem<T, E>[]
}

type SharedOptions = {
  id: string
  name: string
  archived: boolean
  createdDate: string
  uploadedBy: string
  entityConnectionPath?: string[]
  parentId?: string | null
  description?: string
}

export type DirectoryItemType = 'directory' | 'file' | 'link'

export type DirectoryItem<T extends DirectoryItemType = DirectoryItemType, E = {}> = SharedOptions &
  E &
  (T extends 'file'
    ? FileOptions & { type: 'file' }
    : T extends 'link'
    ? LinkOptions & { type: 'link' }
    : T extends 'directory'
    ? DirectoryOptions<DirectoryItemType, E> & { type: 'directory' }
    : never)

export type SortDirection = 'asc' | 'desc'

export type Field =
  | 'name'
  | 'type'
  | 'description'
  | 'fileSize'
  | 'fileType'
  | 'uploadDate'
  | 'uploadedBy'
  | 'entityConnection'
  | 'lastModified'
  | 'width'
  | 'height'
  | 'length'
  | 'resolution'
  | 'nrOfPages'
  | 'creator'
  | 'createdDate'
  | 'lastAuthor'

export type Header = {
  key: Field
  sortDirection?: SortDirection
  sortable?: boolean
  columnWidth?: string
  translationKey: string
}

export type Action = {
  actionName: string
  appliesOn?: DirectoryItemType[]
  icon: string
  labelKey: string
}

export type ActionContext = {
  items: DirectoryItem[]
  currentDirectoryId: string
}

export type ManagerStateUpdate = {
  currentDirectoryId?: string
  state?: FileManagerState
  refresh?: boolean
}

export type ActionResult<T = undefined> =
  | { status: 'success'; data: T; update?: ManagerStateUpdate; message?: string }
  | { status: 'info'; message: string }
  | { status: 'warning'; message: string }
  | { status: 'message'; message: string }
  | { status: 'error'; message: string; error?: Error }

export type Command = {
  action: string
  items: DirectoryItem[]
}

type SharedCreateKeys = 'name' | 'description' | 'entityConnectionPath'
export type DirectoryItemCreate<
  T extends DirectoryItemType = DirectoryItemType,
  E = {}
> = T extends 'file'
  ? Pick<DirectoryItem<'file', E>, SharedCreateKeys> & { type: 'file'; directoryId?: string } & E
  : T extends 'link'
  ? Pick<DirectoryItem<'link', E>, SharedCreateKeys | 'url'> & {
      type: 'link'
      directoryId?: string
    } & E
  : T extends 'directory'
  ? Pick<DirectoryItem<T, E>, SharedCreateKeys | 'parentId'> & {
      type: 'directory'
      directoryId?: string
      children?: DirectoryItemCreate<DirectoryItemType, E>[]
    } & E
  : never

export type DirectoryItemUpdate<T extends DirectoryItemType = DirectoryItemType, E = {}> = Partial<
  Omit<DirectoryItem<T, E>, 'parentId' | 'children' | 'entityConnectionPath'>
> & {
  directoryId?: string | null
  entityConnection?: string
  type: T
}

export type Result<T, E = string> = { ok: true; data: T } | { ok: false; error: E }

export const isOk = <T>(result: Result<T>): result is { ok: true; data: T } => {
  return result.ok === true
}

export const isError = <T>(result: Result<T>): result is { ok: false; error: string } => {
  return result.ok === false
}

export const ok = <T>(data: T): Result<T> => {
  return { ok: true, data }
}

export const error = <T>(error: string): Result<T> => {
  return { ok: false, error }
}
